import { TimePicker } from '@systemeio/components';
import { AlertTriangleIcon, Save01Icon } from '@systemeio/icons';
import { BadRequest } from '@systemeio/util';
import { updateFunnelDeadlineSettings } from 'modules/funnels/funnel/api/funnel-api';
import { DeadlineSettingsCheckboxGroup } from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-checkbox-group';
import DeadlineSettingsTabSkeleton from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-tab-skeleton';
import { useFunnelDeadlineSettings } from 'modules/funnels/funnel/hooks/use-funnel-deadline-settings';
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list';
import { DeadlineSettingsEditInterface, DeadlineSettingsErrorsInterface } from 'modules/funnels/funnel/types/deadline-settings-interface';
import { getAvailableFunnelStepsData, getDeadlineSettingsEditValues, getFunnelStepsToRedirectData, isDeadlineSettingsChanged } from 'modules/funnels/funnel/utils/deadline-settings-tab-utils';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, CheckBoxSwitcher, FormInputNumberGroup, FormSelect, Hint, toast } from 'shared/components';
import { TIME_LABELS, TimeType } from 'shared/components/date-picker/time-delay-picker/constants';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import useUser from 'shared/hooks/use-user';
import { CONTENT_HORIZONTAL_PADDING_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors';
import { getSecondsWithoutTimeZone, getSecondsWithTimeZone } from 'shared/utils/time-utils';
import { twMerge } from 'tailwind-merge';
import DeadlineSettingsItemLayout from './deadline-settings-item-layout';
const defaultValues: DeadlineSettingsEditInterface = {
  enabled: false,
  deadlinePageExpirationInDays: 1,
  expirationFunnelStep: undefined,
  deadlineTime: null,
  deadlineFunnelSteps: []
};
const defaultErrors: DeadlineSettingsErrorsInterface = {
  fields: {
    enabled: '',
    deadlinePageExpirationInDays: '',
    expirationFunnelStep: '',
    deadlineTime: '',
    deadlineFunnelSteps: ''
  }
};
interface DeadlineSettingsTabProps {
  funnelId: FunnelId;
}
function DeadlineSettingsTab({
  funnelId
}: DeadlineSettingsTabProps) {
  const {
    t,
    tICU
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    isDesktop
  } = useMediaQuery();
  const {
    funnelStepsList
  } = useFunnelStepsList(funnelId);
  const {
    deadlineSettings,
    mutate
  } = useFunnelDeadlineSettings(funnelId);
  const [tempState, setTempState] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [isExpirationTime, setIsExpirationTime] = useState(false);
  const [deadlineSeconds, setDeadlineSeconds] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const initialState = useMemo(() => deadlineSettings ? getDeadlineSettingsEditValues(deadlineSettings) : undefined, [deadlineSettings]);
  const initialDeadlineSeconds = useMemo(() =>
  // Seconds within the day, so midnight is a saved `0` and not the absence of a time.
  typeof deadlineSettings?.deadlineTime === 'number' ? getSecondsWithoutTimeZone(deadlineSettings.deadlineTime, user?.timezone) : null, [deadlineSettings?.deadlineTime, user?.timezone]);
  const isChanged = !!initialState && isDeadlineSettingsChanged(tempState, initialState, deadlineSeconds, initialDeadlineSeconds);
  const funnelStepsToRedirectData = getFunnelStepsToRedirectData(funnelStepsList);
  const availableFunnelStepsData = getAvailableFunnelStepsData(funnelStepsList);
  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await fetchWithFieldErrors(async () => {
        const newData = {
          ...tempState,
          deadlineTime: getSecondsWithTimeZone(deadlineSeconds, user?.timezone)
        };
        const response = await updateFunnelDeadlineSettings(funnelId, newData);
        await mutate(data => {
          if (data) {
            return response.data;
          }
        }, false);
      }, setErrors);
      setIsLoading(false);
      toast.success(t('global.changes_saved'));
    } catch (error) {
      setIsLoading(false);
      if (error instanceof BadRequest) {
        if (error.errors.common) {
          const commonErrors = error.errors.common;
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  useEffect(() => {
    if (initialState) {
      setTempState(initialState);
      if (initialDeadlineSeconds !== null) {
        setIsExpirationTime(true);
        setDeadlineSeconds(initialDeadlineSeconds);
      }
    }
  }, [initialState, initialDeadlineSeconds]);
  return <div className="desktop:gap-5 tablet:gap-4 desktop:pb-0 flex flex-col gap-3 pb-18">
      <div className={'flex items-center justify-between gap-5'}>
        <h2>{t('dashboard.funnel.deadline_settings')}</h2>
        <div className={twMerge(CONTENT_HORIZONTAL_PADDING_CLASSNAME, 'bg-bg-primary tablet:static tablet:z-auto tablet:bg-transparent tablet:px-0 desktop:px-0 tablet:py-0 border-border-100 tablet:border-0 fixed inset-x-0 bottom-0 z-40 flex items-center border-t py-4')}>
          <Button className={'w-full'} disabled={!isChanged} onClick={handleConfirm} isLoading={isLoading} variant="primary" size={isDesktop ? 'sm' : 'md'}>
            <Save01Icon />
            {t('dashboard.funnel.deadline_settings_save')}
          </Button>
        </div>
      </div>
      {deadlineSettings ? <>
          <DeadlineSettingsItemLayout>
            <span className={'text-base font-medium'}>{t('dashboard.funnel.deadline_title')}</span>
            <CheckBoxSwitcher label={<Hint label={t('dashboard.funnel.deadline_settings_description')}>
                  {t('dashboard.funnel.deadline_settings_enable')}
                </Hint>} selected={tempState.enabled} onChange={value => setTempState(prev_0 => {
          return {
            ...prev_0,
            enabled: value
          };
        })} disabled={!deadlineSettings} />
            {/* Each field is as wide as its own label (`w-max`), so the row wraps in whatever
                language the two labels stop fitting side by side instead of squeezing them. */}
            <div className={'tablet:flex-row tablet:flex-wrap tablet:items-end tablet:gap-x-10 flex flex-col gap-5'}>
              <FormInputNumberGroup type={'integer'} suffix={tICU(TIME_LABELS[TimeType.DAYS], {
            count: tempState.deadlinePageExpirationInDays
          })} className={'tablet:w-max tablet:min-w-60 tablet:max-w-full'} label={t('dashboard.funnel.deadline_settings_deadline_expiration_title')} error={errors.fields.deadlinePageExpirationInDays} required onChange={value_0 => setTempState(prev_1 => {
            return {
              ...prev_1,
              deadlinePageExpirationInDays: value_0
            };
          })} value={tempState.deadlinePageExpirationInDays} />
              <div className={'tablet:w-max tablet:max-w-full tablet:grow w-full'}>
                <FormSelect label={t('dashboard.funnel.deadline_settings_expiration_funnel_step_title')} value={tempState.expirationFunnelStep} data={funnelStepsToRedirectData} onChange={value_1 => setTempState(prev_2 => {
              return {
                ...prev_2,
                expirationFunnelStep: value_1
              };
            })} required error={errors.fields.expirationFunnelStep} />
              </div>
            </div>
          </DeadlineSettingsItemLayout>
          <DeadlineSettingsItemLayout>
            <>
              <CheckBoxSwitcher label={<Hint label={t('dashboard.funnel.deadline_settings_expiration_time_tooltip')}>
                    {t('dashboard.funnel.deadline_settings_expiration_time_title')}
                  </Hint>} selected={isExpirationTime} onChange={value_2 => {
            if (!value_2) {
              setDeadlineSeconds(null);
            }
            setIsExpirationTime(value_2);
          }} />
              {isExpirationTime && <TimePicker seconds={deadlineSeconds} onChangeSeconds={setDeadlineSeconds} userTimeFormat={user?.timeFormat} />}
            </>
          </DeadlineSettingsItemLayout>
          <DeadlineSettingsItemLayout>
            <Hint trigger={<AlertTriangleIcon className="size-4" />} label={t('dashboard.funnel.deadline_settings_available_funnel_steps_tooltip')}>
              <span className="text-sm">
                {t('dashboard.funnel.deadline_settings_available_funnel_steps_title')}
              </span>
            </Hint>
            <DeadlineSettingsCheckboxGroup ariaLabel={t('dashboard.funnel.deadline_settings_available_funnel_steps_title')} options={availableFunnelStepsData} selected={tempState.deadlineFunnelSteps} onChange={value_3 => {
          const isSelected = tempState.deadlineFunnelSteps.includes(value_3);
          if (isSelected) {
            setTempState(prev_3 => ({
              ...prev_3,
              deadlineFunnelSteps: prev_3.deadlineFunnelSteps.filter(funnelStepId => funnelStepId !== value_3)
            }));
          } else {
            setTempState(prev_4 => ({
              ...prev_4,
              deadlineFunnelSteps: [...prev_4.deadlineFunnelSteps, value_3]
            }));
          }
        }} />
            <FieldErrorAndDescription error={errors.fields.deadlineFunnelSteps} />
          </DeadlineSettingsItemLayout>
        </> : <DeadlineSettingsTabSkeleton />}
      {errors.common && <>
          {errors.common.map((error_0, index) => <p className="text-text-error px-6 text-sm" key={index}>
              {error_0}
            </p>)}
        </>}
    </div>;
}
export default DeadlineSettingsTab;